import React from 'react';
import PropTypes from 'prop-types';
import {action, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Link, withRouter} from 'react-router';
import autobind from 'autobind-decorator';

import DocumentTitle from 'spa/Components/DocumentTitle';
import LoginForm from 'spa/Components/Session/LoginForm';
import SignupForm from 'spa/Components/Session/SignupForm';
import ForgotPasswordForm from 'spa/Components/Session/ForgotPasswordForm';

import forgotIcon from 'spa/images/icons/forgotPass.svg';
import withI18n from 'spa/Components/Context/withI18n';

import './gifts-redeem-session.less';

@withRouter
@inject(({
  store: {
    tracker,
    session,
  },
}) => (
  {
    tracker,
    session,
  }
))
@observer
@withI18n
class GiftsRedeemSession extends React.Component {
  static propTypes = {
    disableTitle: PropTypes.bool,
    disableVoucherField: PropTypes.bool,
    onSessionSuccess: PropTypes.func,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackerLocation: PropTypes.string,
    session: PropTypes.object.isRequired,
    startSignin: PropTypes.bool,
  };

  static defaultProps = {
    disableTitle: false,
    disableVoucherField: false,
    onSessionSuccess: null,
    startSignin: false,
    trackerLocation: 'Redeem Gift',
  }

  componentDidMount() {
    const {trackerLocation} = this.props;

    this.listeners = [
      reaction(
        () => this.sessionPage,
        (sessionPage) => {
          switch (sessionPage) {
          case 'signup':
            this.props.tracker.track(`${trackerLocation} Signup Page`, {
              category: trackerLocation,
            });
            break;
          case 'reset-password':
            this.props.tracker.track(`${trackerLocation} Password Page`, {
              category: trackerLocation,
            });
            break;
          default:
            this.props.tracker.track(`${trackerLocation} Login Page`, {
              category: trackerLocation,
            });
            break;
          }
        },
        {
          name: 'track session page',
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.props.startSignin,
        (startSignin) => {
          if (startSignin) {
            this.sessionPage = 'login';
          }
        },
        {
          name: 'Check startSignin prop on start',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable sessionPage = 'signup';

  @autobind
  @action('onClickSignup - GiftsRedeemSession')
  onClickSignup(event) {
    event.preventDefault();
    this.sessionPage = 'signup';
  }

  @autobind
  @action('onClickLogin - GiftsRedeemSession')
  onClickLogin(event) {
    event.preventDefault();
    this.sessionPage = 'login';
  }

  @autobind
  @action('onClickResetPassword - GiftsRedeemSession')
  onClickResetPassword(event) {
    event.preventDefault();
    this.sessionPage = 'reset-password';
  }

  @autobind
  @action('loginSuccess - GiftsRedeemSession')
  loginSuccess(voucher) {
    if (this.props.onSessionSuccess) {
      this.props.onSessionSuccess(voucher);
    }
  }

  @autobind
  @action('signupSuccess - GiftsRedeemSession')
  signupSuccess(voucher) {
    if (this.props.onSessionSuccess) {
      this.props.onSessionSuccess(voucher);
    }
  }

  render() {
    const {i18n} = this.context;
    const {
      disableTitle,
      disableVoucherField,
      trackerLocation,
    } = this.props;
    const {sessionPage} = this;

    switch (sessionPage) {
    case 'signup':
      return (
        <div className="dark v-container text-center">
          <DocumentTitle title={i18n._x('Register', '<title>')} />
          {!disableTitle && (
            <h1 className="gifts-redeem-session__title">
              {i18n._x('Create your free account to use your gift card', 'Redeem Gift page')}
            </h1>
          )}
          <div className="gifts-redeem-session__container">
            <div className="row gifts-redeem-session__top-section">
              <div className="col-xs-6 gifts-redeem-session__top-left-tab">
                <a
                  href="#"
                  className="gifts-redeem-session__top-link"
                  onClick={this.onClickLogin}>
                  {i18n._x('Sign in', 'Login page')}
                </a>
              </div>
              <div className="col-xs-6 gifts-redeem-session__top-right-tab">
                <span
                  className="gifts-redeem-session__top-link gifts-redeem-session__top-link--active">
                  {i18n._x('Register', 'Redeem Gift page')}
                </span>
              </div>
            </div>
            <div className="container">
              <p>
                {i18n._x('Already have an account?', 'Redeem Gift page')}
                <br />
                <a
                  href="#"
                  className="link"
                  onClick={this.onClickLogin}
                >
                  {i18n._x('Click here to log in.', 'Gift redeem')}
                </a>
              </p>
            </div>
            <div className="container container--medium">
              <SignupForm
                onSignupVoucherSuccess={this.signupSuccess}
                showVoucherField={!disableVoucherField}
                trackingCategory={trackerLocation}
              />
              <p className="text-info text-info--byline gifts-redeem-session__bottom-text">
                {i18n.__react('By creating an account, you agree to our %(termsAndConditions)s and %(privacyPolicy)s', {
                  termsAndConditions: <Link target="_blank" rel="noopener noreferrer" className="link" to={`/${i18n.lc}/legal`}>{i18n._x('terms and conditions', 'Redeem Gift page')}</Link>,
                  privacyPolicy: <Link target="_blank" rel="noopener noreferrer" className="link" to={`/${i18n.lc}/privacy-policy`}>{i18n._x('privacy policy', 'Redeem Gift page')}</Link>,
                })}
              </p>
            </div>
          </div>
        </div>
      );
    case 'reset-password':
      return (
        <div className="dark v-container text-center">
          <DocumentTitle title={i18n._x('Reset Your Password', '<title>')} />
          <div className="gifts-redeem-session__container">
            <div className="row gifts-redeem-session__top-section">
              <div className="col-xs-6 gifts-redeem-session__top-left-tab">
                <a
                  href="#"
                  className="gifts-redeem-session__top-link"
                  onClick={this.onClickLogin}>
                  {i18n._x('Sign in', 'Login page')}
                </a>
              </div>
              <div className="col-xs-6 gifts-redeem-session__top-right-tab">
                <a
                  href="#"
                  className="gifts-redeem-session__top-link"
                  onClick={this.onClickSignup}>
                  {i18n._x('Register', 'Login page')}
                </a>
              </div>
            </div>
            <div className="container">
              <div className="icon-container">
                <img className="icon--forgot" src={forgotIcon} alt="" />
              </div>
              <div className="text-center">
                <h1 className="page-title">{i18n._x('Forgot Your Password?', 'page header')}</h1>

                <p className="text-info">
                  {i18n._x('Enter your account email address below to reset your password', 'Redeem Gift page')}
                </p>
              </div>
            </div>
            <div className="container container--medium">
              <ForgotPasswordForm trackingCategory={trackerLocation} />
              <p className="text-info text-info--byline">
                <a
                  href="#"
                  className="link"
                  onClick={this.onClickLogin}
                >
                  {i18n._x('Go back to login page', 'Redeem Gift page')}
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    default:
      return (
        <div className="dark v-container text-center">
          <DocumentTitle title={i18n._x('Sign in', '<title>')} />
          {!disableTitle && (
            <h1 className="gifts-redeem-session__title">
              {i18n._x('Sign in to use your gift card', 'Redeem Gift page')}
            </h1>
          )}
          <div className="gifts-redeem-session__container">
            <div className="row gifts-redeem-session__top-section">
              <div className="col-xs-6 gifts-redeem-session__top-left-tab">
                <span
                  className="gifts-redeem-session__top-link gifts-redeem-session__top-link--active">
                  {i18n._x('Sign in', 'Login page')}
                </span>
              </div>
              <div className="col-xs-6 gifts-redeem-session__top-right-tab">
                <a
                  href="#"
                  className="gifts-redeem-session__top-link"
                  onClick={this.onClickSignup}>
                  {i18n._x('Register', 'Login page')}
                </a>
              </div>
            </div>
            <div className="container">
              <p>
                {i18n._x("Don't have an account?", 'Redeem Gift page')}
                <br />
                <a
                  href="#"
                  className="link"
                  onClick={this.onClickSignup}
                >
                  {i18n._x('Click here to set up an account', 'Redeem Gift page')}
                </a>
              </p>
            </div>
            <div className="container container--medium">
              <LoginForm
                onLoginSuccess={this.loginSuccess}
                showVoucherField={!disableVoucherField}
                trackingCategory={trackerLocation}
              />

              <div className="row row--with-gutter">
                <div className="col-xs-6">
                  <p className="text-info text-info--byline text-end">
                    <a
                      className="link"
                      href="#"
                      onClick={this.onClickResetPassword}
                    >
                      {i18n._x('Forgot Password?', 'Redeem Gift page')}
                    </a>
                  </p>
                </div>
                <div className="col-xs-6">
                  <p className="text-info text-info--byline text-start">
                    <a
                      className="link"
                      href="#"
                      onClick={this.onClickSignup}
                    >
                      {i18n._x('Create Account', 'Redeem Gift page')}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
}

export default GiftsRedeemSession;



// WEBPACK FOOTER //
// ./spa/Pages/Gifts/GiftsRedeemSession.jsx